import React, { useEffect, useRef, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Link, NavLink, useLocation } from "react-router-dom";
import { FaBars, FaTimes } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import "./App.css";
import KinKeeperLanding from "./components/kinkeeperLanding";
import KinKeeperLogin from "./components/relativeLogin";
import KinKeeperRegister from "./components/memberRegister";
import CreateElderProfile from "./components/createElderProfile";
import ElderProfileView from "./components/elderProfileViewer";
import ElderListPage from "./components/elderListPage";
import ProtectedRoute from "./components/protected";
import Logout from "./components/logout";
import LanguageSwitcher from "./components/LanguageSwitcher";
import PasswordResetPage from "./components/PasswordResetPage";
import logo from "./images/cape.png";

const ROUTES = {
  HOME: "/",
  LOGIN: "/login",
  REGISTER: "/register",
  ELDERS: "/elders",
  ELDER_NEW: "/elders/new",
  ELDER_PROFILE: "/elders/profile"
};

const AppContent = () => {
  const { t } = useTranslation("common");
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const headerRef = useRef(null);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const closeOnEscape = (event) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    const closeOutsideHeader = (event) => {
      if (headerRef.current && !headerRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener("keydown", closeOnEscape);
    document.addEventListener("pointerdown", closeOutsideHeader);
    return () => {
      document.removeEventListener("keydown", closeOnEscape);
      document.removeEventListener("pointerdown", closeOutsideHeader);
    };
  }, [menuOpen]);

  const navigationClass = ({ isActive }) =>
    `nav-link${isActive ? " nav-link-active" : ""}`;

  return (
    <div className="app-shell">
      <a className="skip-link" href="#kinkeeper-main-content">Skip to content</a>
      <header ref={headerRef} className="topbar">
        <Link to={ROUTES.HOME} className="topbar-brand">
          <img src={logo} alt={t("nav.brand")} className="topbar-logo" />
          <span className="topbar-title">{t("nav.brand")}</span>
        </Link>

        <button
          type="button"
          className="topbar-menu-toggle"
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
          aria-controls="kinkeeper-navigation"
          onClick={() => setMenuOpen((open) => !open)}
        >
          {menuOpen ? <FaTimes /> : <FaBars />}
          <span>{menuOpen ? "Close" : "Menu"}</span>
        </button>

        <nav
          id="kinkeeper-navigation"
          className={`topbar-links${menuOpen ? " topbar-links-open" : ""}`}
          aria-label="Primary navigation"
        >
          <NavLink className={navigationClass} to={ROUTES.HOME} end>
            {t("nav.home")}
          </NavLink>
          <NavLink className={navigationClass} to={ROUTES.LOGIN}>
            {t("nav.login")}
          </NavLink>
          <NavLink className={navigationClass} to={ROUTES.REGISTER}>
            {t("nav.register")}
          </NavLink>
          <Logout />
          <LanguageSwitcher />
        </nav>
      </header>

      <main id="kinkeeper-main-content" className="page-offset">
        <Routes>
          <Route path={ROUTES.HOME} element={<KinKeeperLanding />} />
          <Route path={ROUTES.LOGIN} element={<KinKeeperLogin />} />
          <Route path={ROUTES.REGISTER} element={<KinKeeperRegister />} />
          <Route path="/forgot-password" element={<PasswordResetPage />} />
          <Route path="/logout" element={<Logout />} />
          <Route
            path={ROUTES.ELDER_NEW}
            element={
              <ProtectedRoute>
                <CreateElderProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.ELDER_PROFILE}
            element={
              <ProtectedRoute>
                <ElderProfileView />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.ELDERS}
            element={
              <ProtectedRoute>
                <ElderListPage />
              </ProtectedRoute>
            }
          />
        </Routes>
        <footer className="global-footer" aria-label="UberCARE product information">
          <strong>UberCARE for Kin-keepers</strong>
          <span>Family coordination made clearer, wherever care happens.</span>
          <span>© {new Date().getFullYear()} UberCARE</span>
        </footer>
      </main>
    </div>
  );
};

const App = () => {
  return (
    <Router basename="/kinkeeper">
      <AppContent />
    </Router>
  );
};

export default App;
